import React from "react"

import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import { Badge, type BadgeProps } from "@coval/ui"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { AnomalyEventList } from "../components/AnomalyEventList"
import { getCardStyle } from "../components/deepDiveCardStyles"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

interface TimbreDriftRawValues {
  timbre_max_drift_from_anchor?: number
  timbre_max_drift_from_rolling?: number
  timbre_drift_trend?: number
  timbre_shift_p95?: number
}

const CATEGORY = "timbre_drift"
const SEVERITY_THRESHOLD = 0.5

export class TimbreDriftMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  unit = ""
  aggregate_units = ""

  renderMetricType(): string {
    return "Timbre Drift"
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const value = mo.float_value ?? 0
    const resultData = (mo.result as Record<string, unknown> | undefined)
      ?.raw_values as TimbreDriftRawValues | undefined

    let qualityLabel = ""
    let qualityVariant: NonNullable<BadgeProps["variant"]> = "default"

    if (value < 0.3) {
      qualityLabel = "Good"
      qualityVariant = "success"
    } else if (value < 0.5) {
      qualityLabel = "Fair"
      qualityVariant = "info"
    } else if (value < 0.7) {
      qualityLabel = "Warning"
      qualityVariant = "warning"
    } else {
      qualityLabel = "Severe"
      qualityVariant = "danger"
    }

    const anchorDrift = resultData?.timbre_max_drift_from_anchor
    const rollingDrift = resultData?.timbre_max_drift_from_rolling
    const driftTrend = resultData?.timbre_drift_trend
    const p95Shift = resultData?.timbre_shift_p95

    const subvalues = mo.subvalues_by_timestamp ?? []
    const card = getCardStyle(value, 0.25)

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl font-bold">{value.toFixed(3)}</span>
          <Badge variant={qualityVariant}>{qualityLabel}</Badge>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {anchorDrift != null && (
            <div className={`rounded-lg p-3 ${card.bg}`}>
              <div className={card.label}>Anchor Drift</div>
              <div className={card.value}>{anchorDrift.toFixed(3)}</div>
            </div>
          )}
          {rollingDrift != null && (
            <div className={`rounded-lg p-3 ${card.bg}`}>
              <div className={card.label}>Rolling Drift</div>
              <div className={card.value}>{rollingDrift.toFixed(3)}</div>
            </div>
          )}
          {driftTrend != null && (
            <div className={`rounded-lg p-3 ${card.bg}`}>
              <div className={card.label}>Drift Trend</div>
              <div className={card.value}>{driftTrend.toFixed(4)}</div>
            </div>
          )}
          {p95Shift != null && (
            <div className={`rounded-lg p-3 ${card.bg}`}>
              <div className={card.label}>P95 Shift</div>
              <div className={card.value}>{p95Shift.toFixed(3)}</div>
            </div>
          )}
        </div>

        <AnomalyEventList
          subvalues={subvalues}
          severityThreshold={SEVERITY_THRESHOLD}
          chipLabel="Drift"
          title="Drift Events"
          groupByLabel
          onFilterChange={(filtered: SubvalueByTimestamp[] | null) => {
            if (!filtered) {
              simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_REGIONS_HOVER, {
                regions: null,
              })
              return
            }
            const regions: AudioRegionParams[] = filtered.map((sv, idx) => {
              const severity = sv.float_value ?? 0
              const alpha = Math.min(0.2 + severity * 0.5, 0.7)
              return {
                id: `${CATEGORY}_hover:${idx}`,
                start: sv.start_offset,
                end: Math.max(sv.end_offset, sv.start_offset + 0.5),
                color: `rgba(59, 130, 246, ${alpha.toFixed(2)})`,
                drag: false,
                resize: false,
              }
            })
            simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_REGIONS_HOVER, {
              regions,
            })
          }}
        />
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message) {
      return []
    }
    try {
      const subvalues = mo.subvalues_by_timestamp ?? []
      return subvalues
        .filter((item) => (item.float_value ?? 0) >= SEVERITY_THRESHOLD)
        .map((item, idx) => {
          const severity = item.float_value ?? 0
          const alpha = Math.min(0.2 + severity * 0.5, 0.7)
          return {
            id: `${CATEGORY}:${idx}`,
            start: item.start_offset,
            end: Math.max(item.end_offset, item.start_offset + 0.5),
            color: `rgba(59, 130, 246, ${alpha.toFixed(2)})`,
            drag: false,
            resize: false,
          }
        })
    } catch {
      return []
    }
  }
}
